/**
 * Applies the attached demo HTML templates onto the matching /demo-report
 * variants. Runs after the Carter form/sample overlays so template names,
 * listings, verification rows, flags, and court orders win. Variants the
 * attachments don't cover (good / student) are left untouched.
 */
import type { FamilyPredictions } from '#app/utils/application-detail-v2/combined-display'
import type { CombinedReportData } from '#app/utils/application-detail-v2/combined-types'
import type {
	ApplicationDetailData,
	OpenroomRecordRef,
	OpenroomSummary,
	ParsedStepData,
} from '#app/utils/application-detail-v2/types'
import type { VerificationDataItem } from '#app/utils/report-data-helpers'
import templates from './attached-templates.json'

type OverlayReport =
	| { kind: 'detail'; data: ApplicationDetailData }
	| { kind: 'combined'; data: CombinedReportData }

type Field = { k: string; v: string }
type Txn = {
	date: string
	amount: number
	category: string
	description: string
}
type TemplatePerson = {
	name: string | null
	score: number | null
	chips: string[]
	metrics: Field[]
	red: string[]
	yellow: string[]
	fields: Field[]
	note: string
	consent: string
	court: string[]
	income: {
		holder: string
		declared: string | null
		gross: number
		net: number
		rows: Txn[]
	}
	rent: {
		holder: string
		declared: number | null
		rows: Txn[]
	}
}

type SampleBank = {
	institution: string
	holderName: string
	connectedAt: string
}

const MONTHS: Record<string, string> = {
	jan: '01',
	feb: '02',
	mar: '03',
	apr: '04',
	may: '05',
	jun: '06',
	jul: '07',
	aug: '08',
	sep: '09',
	oct: '10',
	nov: '11',
	dec: '12',
}

const COURT_RECORDS: OpenroomRecordRef[] = [
	{
		url: '/demo-court/LTB-L-030269-26-EX.pdf',
		title: 'Record 1',
		name: 'Record 1',
		source: 'ltb',
		matchStrength: 'strong',
	},
	{
		url: '/demo-court/LTB-L-086782-25.pdf',
		title: 'Record 2',
		name: 'Record 2',
		source: 'ltb',
		matchStrength: 'strong',
	},
]

const emptyLoan = (type: string) => ({
	type,
	used: '0%',
	limit: 0,
	balance: 0,
	pastDue: 0,
	paymentStatus: 'N/A',
	monthlyPayments: 0,
})

const BAD_DEBT = {
	details: [
		{
			type: 'Revolving Credit',
			used: '73%',
			limit: 19595,
			balance: 14251,
			pastDue: 0,
			paymentStatus: 'Making Regular Payments Under Consolidation Order',
			monthlyPayments: 20,
		},
		emptyLoan('Auto Loan'),
		emptyLoan('Student Loan'),
	],
	totalDebtOwing: 23407,
	monthlyDebtPayment: 237,
}

const GUARANTOR_DEBT = {
	details: [
		{
			type: 'Revolving Credit',
			used: '5%',
			limit: 20100,
			balance: 995,
			pastDue: 0,
			paymentStatus: 'Up To Date',
			monthlyPayments: 10,
		},
		emptyLoan('Auto Loan'),
		emptyLoan('Student Loan'),
	],
	totalDebtOwing: 995,
	monthlyDebtPayment: 10,
}

const DOREEN_DEBT = {
	details: [
		{
			type: 'Revolving Credit',
			used: '28%',
			limit: 6000,
			balance: 1706,
			pastDue: 0,
			paymentStatus: 'Up To Date',
			monthlyPayments: 44,
		},
		emptyLoan('Auto Loan'),
		emptyLoan('Student Loan'),
	],
	totalDebtOwing: 1706,
	monthlyDebtPayment: 44,
}

const NOLAN_DEBT = {
	details: [
		{
			type: 'Revolving Credit',
			used: '80%',
			limit: 19500,
			balance: 15661,
			pastDue: 0,
			paymentStatus: 'Up To Date',
			monthlyPayments: 312,
		},
		emptyLoan('Auto Loan'),
		emptyLoan('Student Loan'),
	],
	totalDebtOwing: 15661,
	monthlyDebtPayment: 312,
}

const BAD_COLLECTIONS = {
	data: [
		{
			date: '01 Mar 2023',
			amount: 0,
			status: 'Paid',
			remarks: 'Paid Collection, Last payment: Sep, 2021',
			creditor: 'SEZZLE CANADA CORP',
			originalAmount: 550,
		},
	],
}

const BAD_WRITEOFFS = {
	data: [
		{
			date: 'Oct 2024',
			amount: 9156,
			status: 'Unpaid',
			remarks:
				'WRITTEN-OFF, AUTO, 60 days past due - 2024-12, 60 days past due - 2024-11; 30 days past due - 2024-10',
			creditor: 'Auto Financing',
			originalAmount: 0,
		},
		{
			date: 'Oct 2024',
			amount: 0,
			status: 'Paid',
			remarks:
				'WRITTEN-OFF, CREDIT COUNSELLING, Making repayments - 2026-02, 120+ days past due - 2026-01; 90 days past due - 2025-12',
			creditor: 'National Credit Card Companies',
			originalAmount: 2500,
		},
		{
			date: 'Jan 2022',
			amount: 0,
			status: 'Paid',
			remarks:
				'WRITTEN-OFF, Making repayments - 2022-12, Making repayments - 2022-11; Making repayments - 2022-10',
			creditor: 'Banks',
			originalAmount: 1527,
		},
		{
			date: 'Jan 2022',
			amount: 0,
			status: 'Paid',
			remarks:
				'WRITTEN-OFF, 120+ days past due - 2022-07, 120+ days past due - 2022-06; 120+ days past due - 2022-05',
			creditor: 'Savings & Loans Assocations',
			originalAmount: 500,
		},
	],
}

const BAD_BANKRUPTCY = {
	data: [
		{
			assets: 48000,
			status: 'Unpaid',
			remarks: 'OFF SUP BKRPT, Proposal to creditors',
			trustee: '400118275 MSI SPE…',
			reported: '2021-12-01',
			liability: 99099,
		},
	],
}

const NOLAN_COLLECTIONS = {
	data: [
		{
			date: '01 Oct 2023',
			amount: 0,
			status: 'Paid',
			remarks: 'Paid Collection, Last payment: Nov, 2022',
			creditor: 'QUICKFUNDS CANADA',
			originalAmount: 340,
		},
	],
}

const EMPTY_CREDIT_EVENTS = { data: [] }

type DetailSpec = {
	person: TemplatePerson
	chips: string[]
	flexible: boolean
	household: 'clear' | 'fields' | 'keep'
	bank: SampleBank
	incomeStatus: string
	rentStatus: string
	guarantorRequired: boolean
	debt: typeof BAD_DEBT | null
	collections: typeof BAD_COLLECTIONS | null
	writeoffs: typeof BAD_WRITEOFFS | null
	bankruptcy: typeof BAD_BANKRUPTCY | null
	court: OpenroomSummary | null
	/** Template has no AI narrative — hide the card instead of inventing one. */
	hideAiSummary: boolean
	/** Cap the income chart axis. Unset keeps a longer deposit span. */
	chartMonths?: number
	/** Newest income-chart month (YYYY-MM). Unset uses the latest deposit. */
	chartEnd?: string
	/** Fixed account-average divisor. Unset uses months with deposits. */
	averageMonths?: number
	/** Pin the rent month grid (YYYY-MM). Unset uses the latest payment. */
	rentGridEnd?: string
	/** Caption uses the application annual figure, not gross × 12. */
	alignSalaryCaption?: boolean
}

const prescreen = templates.prescreen as TemplatePerson
const bad = templates.bad as TemplatePerson
const guarantor = templates.guarantor as TemplatePerson
const primary = templates.combined.primary as TemplatePerson
const roommate = templates.combined.roommate as TemplatePerson
const householdChips = templates.combined.chips

const noRecords: OpenroomSummary = {
	status: 'completed',
	url: null,
	records: [],
}

const courtFound: OpenroomSummary = {
	status: 'records_found',
	url: COURT_RECORDS[0]?.url ?? null,
	records: COURT_RECORDS,
}

const DETAIL: Partial<Record<string, DetailSpec>> = {
	// Frozen: Marisol Reyes matches pre-screen-report.html. Align other
	// variants in their own spec — do not retune this block, chartEnd,
	// averageMonths, or shared appliers in a way that changes this report.
	santos: spec(prescreen, prescreen.chips, {
		household: 'clear',
		bank: bank('TD', 'MARISOL D. R.', '2026-08-25T19:41:00-04:00'),
		incomeStatus: 'Completed',
		rentStatus: 'Completed',
		court: null,
		// Chart is Aug '26–Sep '25. Average still divides by the 13-month
		// connection window so Side Gig does not add a 14th month.
		chartMonths: 12,
		chartEnd: '2026-08',
		averageMonths: 13,
		alignSalaryCaption: true,
	}),
	// Frozen: Trevor Mackie matches bad-full-screen-report.html. Align other
	// variants in their own spec — do not retune this block, chartMonths,
	// rentGridEnd, or shared appliers in a way that changes this report.
	malone: spec(bad, bad.chips, {
		flexible: true,
		household: 'fields',
		bank: bank('Tangerine', 'TREVOR W. M.', '2026-08-27T19:34:00-04:00'),
		incomeStatus: 'Inconsistent',
		rentStatus: 'Inconsistent',
		guarantorRequired: true,
		debt: BAD_DEBT,
		collections: BAD_COLLECTIONS,
		writeoffs: BAD_WRITEOFFS,
		bankruptcy: BAD_BANKRUPTCY,
		court: courtFound,
		hideAiSummary: true,
		chartMonths: 12,
		rentGridEnd: '2026-08',
		alignSalaryCaption: true,
	}),
	// Frozen: Ramesh Bhandari matches guarantor-report.html. Align other
	// variants in their own spec — do not retune this block or the shared
	// appliers in a way that changes this report.
	'haddad-karim': spec(guarantor, guarantor.chips, {
		household: 'keep',
		bank: bank('TD', 'RAMESH B.', '2026-08-13T17:51:00-04:00'),
		incomeStatus: 'Completed',
		rentStatus: 'Completed',
		debt: GUARANTOR_DEBT,
		collections: EMPTY_CREDIT_EVENTS,
		writeoffs: EMPTY_CREDIT_EVENTS,
		court: noRecords,
		hideAiSummary: true,
	}),
	// Strong Applicant uses Doreen Halliday's individual view from
	// combined-report.html. Do not retune the frozen household or other specs.
	whitfield: spec(primary, householdChips, {
		household: 'clear',
		bank: bank('Scotia', 'DOREEN H.', '2026-09-15T13:02:00-04:00'),
		incomeStatus: 'Completed',
		rentStatus: 'Completed',
		debt: DOREEN_DEBT,
		collections: EMPTY_CREDIT_EVENTS,
		writeoffs: EMPTY_CREDIT_EVENTS,
		court: noRecords,
		hideAiSummary: true,
		alignSalaryCaption: true,
	}),
	'haddad-omar': spec(primary, householdChips, {
		household: 'clear',
		bank: bank('Scotia', 'DOREEN H.', '2026-09-15T13:02:00-04:00'),
		incomeStatus: 'Completed',
		rentStatus: 'Completed',
		debt: DOREEN_DEBT,
		collections: EMPTY_CREDIT_EVENTS,
		writeoffs: EMPTY_CREDIT_EVENTS,
		court: noRecords,
	}),
	'haddad-layla': spec(roommate, householdChips, {
		household: 'clear',
		bank: bank('TD', 'NOLAN R. H.', '2026-09-16T00:54:00-04:00'),
		incomeStatus: 'Completed',
		rentStatus: 'Not Found',
		guarantorRequired: true,
		debt: NOLAN_DEBT,
		collections: NOLAN_COLLECTIONS,
		writeoffs: EMPTY_CREDIT_EVENTS,
		court: noRecords,
	}),
}

function bank(
	institution: string,
	holderName: string,
	connectedAt: string,
): SampleBank {
	return { institution, holderName, connectedAt }
}

function spec(
	person: TemplatePerson,
	chips: string[],
	extra: Partial<DetailSpec> = {},
): DetailSpec {
	return {
		person,
		chips,
		flexible: false,
		household: 'keep',
		bank: bank('TD', person.income.holder, '2026-08-01T12:00:00-04:00'),
		incomeStatus: 'Completed',
		rentStatus: 'Completed',
		guarantorRequired: false,
		debt: null,
		collections: null,
		writeoffs: null,
		bankruptcy: null,
		court: null,
		hideAiSummary: false,
		...extra,
	}
}

function asRecord(value: unknown): Record<string, unknown> {
	if (value != null && typeof value === 'object' && !Array.isArray(value)) {
		return { ...(value as Record<string, unknown>) }
	}
	return {}
}

function blank(value: string | undefined): boolean {
	const text = (value ?? '').trim()
	return text === '' || text === '-' || text === '—'
}

function values(person: TemplatePerson, key: string): string[] {
	const want = key.toLowerCase()
	return person.fields
		.filter((field) => field.k.toLowerCase() === want && !blank(field.v))
		.map((field) => field.v)
}

function field(person: TemplatePerson, key: string, index = 0): string {
	return values(person, key)[index] ?? ''
}

function metric(person: TemplatePerson, key: string): string {
	return person.metrics.find((item) => item.k === key)?.v ?? ''
}

function digits(value: string): string {
	return value.replace(/[^0-9]/g, '')
}

function e164(value: string): string {
	const numeric = digits(value)
	if (numeric.length === 10) return `+1${numeric}`
	if (numeric.length === 11 && numeric.startsWith('1')) return `+${numeric}`
	return value
}

function isoDate(label: string): string {
	const named = label.match(/([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})/)
	if (named) {
		const month = MONTHS[named[1].slice(0, 3).toLowerCase()]
		if (month) {
			return `${named[3]}-${month}-${named[2].padStart(2, '0')}`
		}
	}
	const dayFirst = label.match(/(\d{1,2})\s+([A-Za-z]+),?\s+(\d{4})/)
	if (dayFirst) {
		const month = MONTHS[dayFirst[2].slice(0, 3).toLowerCase()]
		if (month) {
			return `${dayFirst[3]}-${month}-${dayFirst[1].padStart(2, '0')}`
		}
	}
	return label
}

function consentIso(label: string): string | null {
	const match = label.match(
		/(\d{1,2})\s+([A-Za-z]+),?\s+(\d{4}),?\s+(\d{1,2}):(\d{2})\s*([AP]M)/i,
	)
	if (!match) return null
	const month = MONTHS[match[2].slice(0, 3).toLowerCase()]
	if (!month) return null
	let hour = Number(match[4]) % 12
	if (match[6].toUpperCase() === 'PM') hour += 12
	const hh = String(hour).padStart(2, '0')
	return `${match[3]}-${month}-${match[1].padStart(2, '0')}T${hh}:${match[5]}:00-04:00`
}

function rentRange(label: string): { min: number; max: number } | null {
	const match = label.replace(/,/g, '').match(/(\d+)\s*[–-]\s*\$?\s*(\d+)/)
	if (!match) return null
	return { min: Number(match[1]), max: Number(match[2]) }
}

function listingFrom(chips: string[]) {
	const beds = chips.find((chip) => /bed/i.test(chip))
	const baths = chips.find((chip) => /bath/i.test(chip))
	const unit = chips.find((chip) => /^unit\b/i.test(chip))
	const rent = chips.find((chip) => chip.includes('$'))
	const title = chips[0] ?? ''
	return {
		title,
		address: title,
		bedrooms: beds ? Number.parseInt(beds, 10) : null,
		bathrooms: baths ? Number.parseInt(baths, 10) : null,
		unitNumber: unit?.replace(/^unit\s*/i, '') ?? '',
		monthlyRent: rent ? Number(digits(rent)) : null,
	}
}

function splitName(name: string): { firstName: string; lastName: string } {
	const parts = name.trim().split(/\s+/)
	return {
		firstName: parts[0] ?? '',
		lastName: parts.slice(1).join(' '),
	}
}

function noteText(note: string): string {
	return note.replace(/^[“"]|[”"]$/g, '').trim()
}

function txns(rows: Txn[], holder: string): VerificationDataItem[] {
	return rows.map((row) => ({
		date: row.date,
		amount: row.amount,
		category: row.category === 'sidegig' ? 'side_gig' : row.category,
		description: row.description,
		holder,
		currency: 'CAD',
	}))
}

function patchBank(
	step: ParsedStepData['verify-income'],
	sample: SampleBank,
): ParsedStepData['verify-income'] {
	const base = asRecord(step)
	const accounts = Array.isArray(base.accounts) ? [...base.accounts] : [{}]
	const first = asRecord(accounts[0])
	const summary = asRecord(first.summary)
	accounts[0] = {
		...first,
		status: 'success',
		addedAt: sample.connectedAt,
		verifiedAt: sample.connectedAt,
		isPrimary: true,
		summary: {
			...summary,
			institution: sample.institution,
			holderName: sample.holderName,
		},
	}
	return {
		...base,
		completed: true,
		bankVerified: true,
		accounts,
	} as ParsedStepData['verify-income']
}

function occupancy(person: TemplatePerson, mode: DetailSpec['household']) {
	if (mode === 'clear') {
		return { children: [], numChildren: '0', pets: [], vehicles: [] }
	}
	if (mode !== 'fields') return null
	const pets = person.fields
		.filter((item) => /^pet\s+\d+/i.test(item.k))
		.map((item, index) => {
			const weight = item.v.match(/^([^()]+)/)?.[1]?.trim() ?? item.v
			const type = item.v
				.match(/\(([^)]+)\)/)?.[1]
				?.trim()
				.toLowerCase()
			return {
				id: `pet-${index + 1}`,
				petType: type ?? 'dog',
				weight,
			}
		})
	const vehicles = person.fields
		.filter((item) => /^vehicle\s+\d+/i.test(item.k))
		.map((item, index) => {
			const make = item.v.match(/^([^()]+)/)?.[1]?.trim() ?? item.v
			const type = /truck|van/i.test(item.v) ? 'truck' : 'car'
			return {
				id: `vehicle-${index + 1}`,
				vehicleType: type,
				make,
			}
		})
	return { children: [], numChildren: '0', pets, vehicles }
}

function applyAdult(
	step: ParsedStepData['property'],
	person: TemplatePerson,
	household: DetailSpec['household'],
): ParsedStepData['property'] {
	if (!step) return step
	const name = person.name ?? ''
	const { firstName, lastName } = splitName(
		field(person, 'First Name')
			? `${field(person, 'First Name')} ${field(person, 'Last Name')}`
			: name,
	)
	const email = field(person, 'Email')
	const phone = e164(field(person, 'Phone'))
	const dob = isoDate(
		field(person, 'Date Of Birth') || field(person, 'Date of birth'),
	)
	const existing = step.adults?.[0]
	const adult = {
		...existing,
		id: existing?.id ?? 'adult-1',
		firstName,
		lastName,
		name,
		email,
		phone,
		phoneCountryCode: 'CA',
		dateOfBirth: dob,
		isPrimary: true,
	}
	const home = occupancy(person, household)
	return {
		...step,
		adults: [adult],
		guarantor: undefined,
		guarantors: [],
		...(home ?? {}),
	}
}

function applyRental(
	rental: ParsedStepData['rental'],
	person: TemplatePerson,
): ParsedStepData['rental'] {
	if (!rental) return rental
	const next = { ...rental }
	const landlord = field(person, 'Landlord Name')
	if (landlord) {
		next.currentLandlordName = landlord
		next.currentLandlordPhone = e164(field(person, 'Phone Number'))
		next.currentLandlordEmail = field(person, 'Email', 1)
		next.currentRent = digits(field(person, 'Current Rent (CAD)'))
		next.currentPaymentMethod = field(person, 'Rental Payment Method')
		next.currentYearsLived = field(person, 'Total Duration Lived At Address')
		next.currentAddress = field(person, 'Address')
		const previous = field(person, 'Previous Landlord Name')
		if (previous) {
			next.previousLandlordName = previous
			next.previousLandlordPhone = e164(field(person, 'Phone Number', 1))
			next.previousLandlordEmail = field(person, 'Email', 2)
			next.previousRent = digits(field(person, 'Previous Rent (CAD)'))
			next.previousPaymentMethod = field(person, 'Rental Payment Method', 1)
			next.previousAddress = field(person, 'Address', 1)
			next.previousMoveInDate = isoDate(field(person, 'Move In Date'))
			next.previousMoveOutDate = isoDate(field(person, 'Move Out Date'))
		} else {
			next.previousLandlordName = ''
			next.previousLandlordPhone = ''
			next.previousLandlordEmail = ''
			next.previousAddress = ''
			next.previousRent = ''
			next.previousPaymentMethod = ''
			next.previousMoveInDate = ''
			next.previousMoveOutDate = ''
		}
	}
	const address = field(person, 'Current address')
	if (address) {
		// Guarantor template is Own Home, not a currently-renting card.
		const duration =
			field(person, 'Total duration lived at address') ||
			field(person, 'Total Duration Lived At Address')
		next.rentalStatus = 'own-home'
		next.currentAddressFirstRenter = address
		next.yearsLivedAtAddress = duration
		next.currentAddress = ''
		next.currentLandlordName = ''
		next.currentLandlordPhone = ''
		next.currentLandlordEmail = ''
		next.currentRent = ''
		next.currentPaymentMethod = ''
		next.currentYearsLived = ''
		next.currentLivingSituation = ''
		next.customQuestionAnswers = undefined
	}
	// Template has no previous tenancy — don't keep the dummy previous rent.
	if (
		!field(person, 'Previous Rent (CAD)') &&
		!field(person, 'Previous Landlord Name')
	) {
		next.previousRent = ''
		next.previousLandlordName = ''
		next.previousLandlordPhone = ''
		next.previousLandlordEmail = ''
		next.previousAddress = ''
		next.previousPaymentMethod = ''
		next.previousMoveInDate = ''
		next.previousMoveOutDate = ''
	}
	return next
}

function applyEmployment(
	employment: ParsedStepData['employment'],
	person: TemplatePerson,
): ParsedStepData['employment'] {
	const company = field(person, 'Company')
	if (!employment || !company) return employment
	return {
		...employment,
		employmentStatus: 'currently-employed',
		companyName: company,
		occupation: field(person, 'Role'),
		annualIncome: digits(
			field(person, 'Annual Income') || field(person, 'Annual income'),
		),
		yearsOfEmployment: field(person, 'Tenure'),
		employmentType:
			field(person, 'Employment Type') || field(person, 'Employment type'),
		companyPhone: e164(
			field(person, 'Company Phone') || field(person, 'Company phone'),
		),
		companyPhoneCountryCode: 'CA',
		workAddress: field(person, 'Work Address') || field(person, 'Work address'),
		immigrationStatus:
			field(person, 'Work Authorization Status') ||
			field(person, 'Work authorization status'),
		immigrationStatusExpiration: '',
		// These templates don't list children, so the form never asked
		// maternity. A leftover "No" would still show the yes/no field.
		maternityLeave: '',
		additionalEmployments: [],
		currentSourceOfIncome: '',
		sourceOfIncome: '',
		governmentAssistance: '',
		customQuestionAnswers: undefined,
		previousCompanyName: '',
		previousOccupation: '',
		previousAnnualIncome: '',
		previousCompanyPhone: '',
		previousWorkAddress: '',
		previousYearsOfEmployment: '',
		previousEmploymentType: '',
		previousSourceOfIncome: '',
		previousEmploymentStartDate: '',
		previousEmploymentEndDate: '',
	}
}

function applyListingStep(
	step: ParsedStepData['property'],
	chips: string[],
	moveIn: string,
	flexible: boolean,
): ParsedStepData['property'] {
	if (!step) return step
	const listed = listingFrom(chips)
	return {
		...step,
		unitNumber: listed.unitNumber,
		bedrooms: listed.bedrooms ?? step.bedrooms,
		bathrooms: listed.bathrooms ?? step.bathrooms,
		monthlyRent: listed.monthlyRent ?? step.monthlyRent,
		moveInDate: moveIn || step.moveInDate,
		moveInDateFlexible: flexible ? 'yes' : step.moveInDateFlexible,
	}
}

function summaryFor(person: TemplatePerson, job: string): string {
	const range = metric(person, 'Rental Range Eligibility')
	const savings = metric(person, 'Savings & Investments')
	const parts = [person.name ?? 'The applicant']
	if (job) parts.push(job)
	if (person.score != null) {
		parts.push(`a TrueTenant score of ${person.score} out of 100`)
	}
	if (range) parts.push(`an eligible monthly rent range of ${range}`)
	if (savings) parts.push(`savings and investments of ${savings}`)
	return `${parts.join(', ')}.`
}

function applyDetail(
	data: ApplicationDetailData,
	detail: DetailSpec,
): ApplicationDetailData {
	const person = detail.person
	const listed = listingFrom(detail.chips)
	const moveIn = isoDate(metric(person, 'Move-In Date'))
	const range = rentRange(metric(person, 'Rental Range Eligibility'))
	const savings = Number(digits(metric(person, 'Savings & Investments'))) || 0
	const holder = person.income.holder || person.rent.holder || person.name || ''
	const email = field(person, 'Email')
	const phone = e164(field(person, 'Phone'))
	const results = data.results
	const ai = asRecord(results?.aiReportData)
	const predictions = asRecord(ai.predictions)
	const vs = asRecord(predictions.verificationSummary)
	const job = [field(person, 'Role'), field(person, 'Company')]
		.filter(Boolean)
		.join(' at ')
	predictions.applicant = {
		...asRecord(predictions.applicant),
		name: (person.name ?? '').toUpperCase(),
		rentRange: range ? { ...range, currency: 'CAD' } : undefined,
		trueTenantScore: person.score,
		guarantorRecommendation: detail.guarantorRequired
			? 'Guarantor Recommended'
			: 'Guarantor not Recommended',
	}
	if (person.fields.some((item) => item.v === 'Guarantor')) {
		predictions.guarantor = {
			...asRecord(predictions.guarantor),
			name: (person.name ?? '').toUpperCase(),
		}
	}
	predictions.riskFlags = {
		red: person.red.map((message) => ({ level: 'High', message })),
		yellow: person.yellow.map((message) => ({ level: 'Medium', message })),
	}
	if (savings) {
		predictions.financialSummary = {
			...asRecord(predictions.financialSummary),
			currency: 'CAD',
			savingsInvestments: savings,
		}
	}
	if (detail.debt) predictions.debtSummary = detail.debt
	if (detail.collections) predictions.collections = detail.collections
	if (detail.writeoffs) predictions.writtenOffAccounts = detail.writeoffs
	if (detail.bankruptcy) predictions.bankruptcyAndInsolvency = detail.bankruptcy
	const statedAnnual = Number(
		digits(field(person, 'Annual Income') || field(person, 'Annual income')),
	)
	const grossMonthly =
		detail.alignSalaryCaption && statedAnnual > 0
			? statedAnnual / 12
			: person.income.gross || null
	predictions.verificationSummary = {
		...vs,
		incomeVerification: {
			...asRecord(vs.incomeVerification),
			status: detail.incomeStatus,
			details: 'Income deposits from the linked account',
			data: txns(person.income.rows, holder),
			...(detail.chartMonths ? { chartMonths: detail.chartMonths } : {}),
			...(detail.chartEnd ? { chartEnd: detail.chartEnd } : {}),
			...(detail.averageMonths ? { averageMonths: detail.averageMonths } : {}),
			declaredIncome: {
				declaredCurrent: {
					netMonthly: person.income.net || null,
					grossMonthly,
				},
				declaredPrevious: null,
			},
		},
		rentVerification: {
			...asRecord(vs.rentVerification),
			status: detail.rentStatus,
			details: '',
			data: txns(person.rent.rows, holder),
			...(detail.rentGridEnd ? { rentGridEnd: detail.rentGridEnd } : {}),
		},
	}
	const aiSummary = detail.hideAiSummary
		? ''
		: summaryFor(person, job ? `employed as ${job}` : '')
	predictions.aiGeneratedSummary = aiSummary
	if (detail.hideAiSummary) predictions.summary = ''
	const propertyStep = applyListingStep(
		applyAdult(data.steps.property, person, detail.household),
		detail.chips,
		moveIn,
		detail.flexible,
	)
	const consentedAt = consentIso(person.consent)
	const note = noteText(person.note)
	return {
		...data,
		consentedAt: consentedAt ?? data.consentedAt,
		application: {
			...data.application,
			applicantName: person.name ?? data.application.applicantName,
			applicantEmail: email || data.application.applicantEmail,
			applicantPhone: phone || data.application.applicantPhone,
			score: person.score,
			guarantorRequired: detail.guarantorRequired,
		},
		property: {
			...data.property,
			title: listed.title || data.property.title,
			address: listed.address || data.property.address,
			bedrooms: listed.bedrooms,
			bathrooms: listed.bathrooms,
			unitNumber: listed.unitNumber || data.property.unitNumber,
			monthlyRent: listed.monthlyRent ?? data.property.monthlyRent,
		},
		// Applicant switcher still read fixture Haddad names after the
		// Halliday template overlay — remap so Individual matches Combined.
		groupMembers: remapHaddadGroupMembers(data.groupMembers),
		idVerification: data.idVerification
			? {
					...data.idVerification,
					documentName: person.name ?? data.idVerification.documentName,
				}
			: data.idVerification,
		openroom: detail.court ?? data.openroom,
		steps: {
			...data.steps,
			property: propertyStep,
			rental: applyRental(data.steps.rental, person),
			employment: applyEmployment(data.steps.employment, person),
			review: note
				? { ...data.steps.review, additionalNotes: note }
				: data.steps.review,
			'verify-income': patchBank(data.steps['verify-income'], detail.bank),
		},
		results: results
			? {
					...results,
					overallScore: person.score,
					guarantorRequired: detail.guarantorRequired,
					aiReportData: {
						...ai,
						predictions,
						aiGeneratedSummary: predictions.aiGeneratedSummary,
					},
				}
			: results,
	}
}

/** Individual switcher names/scores for the Halliday household templates. */
function remapHaddadGroupMembers(
	members: ApplicationDetailData['groupMembers'],
): ApplicationDetailData['groupMembers'] {
	const byId: Record<
		string,
		{
			person: TemplatePerson
			relationshipLabel: string
			guarantorRequired: boolean
			guarantorAcceptable?: boolean | null
		}
	> = {
		'demo-haddad-omar': {
			person: primary,
			relationshipLabel: 'Primary',
			guarantorRequired: false,
		},
		'demo-haddad-layla': {
			person: roommate,
			relationshipLabel: 'Family',
			guarantorRequired: true,
		},
		'demo-haddad-karim': {
			person: guarantor,
			relationshipLabel: 'Guarantor',
			guarantorRequired: false,
			guarantorAcceptable: true,
		},
	}
	if (!members.some((member) => member.applicationId in byId)) return members
	return members.map((member) => {
		const overlay = byId[member.applicationId]
		if (!overlay) return member
		const nextEmail =
			field(overlay.person, 'Email') || field(overlay.person, 'email')
		const nextPhone = e164(
			field(overlay.person, 'Phone') || field(overlay.person, 'phone'),
		)
		return {
			...member,
			displayName: overlay.person.name ?? member.displayName,
			email: nextEmail || member.email,
			phone: nextPhone || member.phone,
			score: overlay.person.score,
			relationshipLabel: overlay.relationshipLabel,
			guarantorRequired: overlay.guarantorRequired,
			guarantorAcceptable:
				overlay.guarantorAcceptable !== undefined
					? overlay.guarantorAcceptable
					: member.guarantorAcceptable,
		}
	})
}

function applyCombined(data: CombinedReportData): CombinedReportData {
	const listed = listingFrom(householdChips)
	const range = { min: 3450, max: 4010 }
	const familyPredictions: FamilyPredictions = {
		familyScore: 88,
		rentRange: range,
		guarantorScore: null,
		guarantorRecommendation: null,
		relationshipType: 'family',
		riskFlags: { red: [], yellow: [] },
		savingsInvestments: 26506,
		verificationSummary: {
			id: { status: 'Completed' },
			income: { status: 'Completed' },
			rent: { status: 'Inconsistent' },
		},
		// Attached combined view has no AI narrative.
		summary: '',
	}
	const api = asRecord(data.familyScore?.apiResponse)
	const predictions = Array.isArray(api.predictions)
		? [...api.predictions]
		: [{}]
	predictions[0] = {
		...asRecord(predictions[0]),
		rentRange: { ...range, currency: 'CAD' },
		riskFlags: { red: [], yellow: [], family_rent_check_passed: true },
		financialSummary: { currency: 'CAD', savingsInvestments: 26506 },
		trueTenantFamilyScore: 88,
		guarantorRecommendation: null,
		summary: '',
	}
	const moveIn = '2026-10-01'
	const step = (source: ParsedStepData['property']) =>
		source
			? {
					...applyListingStep(source, householdChips, moveIn, false),
					adults: [
						{
							id: 'adult-doreen',
							firstName: 'Doreen',
							lastName: 'Halliday',
							name: 'Doreen Halliday',
							email: 'doreen.halliday@example.com',
							phone: '+14165550129',
							phoneCountryCode: 'CA',
							dateOfBirth: '1945-03-05',
							isPrimary: true,
						},
						{
							id: 'adult-nolan',
							firstName: 'Nolan',
							lastName: 'Halliday',
							name: 'Nolan Halliday',
							email: 'nolan.halliday@example.com',
							phone: '+16475550143',
							phoneCountryCode: 'CA',
							dateOfBirth: '1977-07-09',
							relationship: 'Family',
						},
					],
					children: [],
					pets: [],
					vehicles: [],
					guarantor: undefined,
					guarantors: [],
				}
			: source
	return {
		...data,
		application: {
			...data.application,
			applicantName: 'Doreen Halliday',
			applicantEmail: 'doreen.halliday@example.com',
			applicantPhone: '+14165550129',
			score: 88,
			guarantorRequired: false,
		},
		property: {
			...data.property,
			title: listed.title,
			address: listed.address,
			bedrooms: listed.bedrooms,
			bathrooms: listed.bathrooms,
			unitNumber: listed.unitNumber,
			monthlyRent: listed.monthlyRent ?? data.property.monthlyRent,
		},
		primaryPropertyStep: step(data.primaryPropertyStep),
		householdPropertyStep: step(data.householdPropertyStep),
		members: data.members
			.filter((member) => member.applicationId !== 'demo-haddad-karim')
			.map((member) => {
				if (member.applicationId === 'demo-haddad-omar') {
					return {
						...member,
						displayName: 'Doreen Halliday',
						relationshipLabel: 'Primary',
						email: 'doreen.halliday@example.com',
						phone: '+14165550129',
						score: 95,
						guarantorRequired: false,
					}
				}
				if (member.applicationId === 'demo-haddad-layla') {
					return {
						...member,
						displayName: 'Nolan Halliday',
						relationshipLabel: 'Family',
						email: 'nolan.halliday@example.com',
						phone: '+16475550143',
						score: 61,
						guarantorRequired: true,
					}
				}
				return member
			}),
		memberFlags: {
			...data.memberFlags,
			'demo-haddad-omar': { red: primary.red, yellow: primary.yellow },
			'demo-haddad-layla': { red: roommate.red, yellow: roommate.yellow },
		},
		memberCourtRecords: {
			...data.memberCourtRecords,
			'demo-haddad-omar': noRecords,
			'demo-haddad-layla': noRecords,
		},
		familyScore: data.familyScore
			? {
					...data.familyScore,
					score: 88,
					apiResponse: { ...api, predictions },
				}
			: data.familyScore,
		familyPredictions,
		// Template hero is score only — no Guarantor Recommended chip.
		guarantorRequests: {
			perApplicant: [],
			accepted: 0,
			total: 0,
			requested: false,
		},
		// Both combined consent rows use Doreen's stamp.
		memberConsents: {
			...data.memberConsents,
			'demo-haddad-omar': {
				...data.memberConsents['demo-haddad-omar'],
				consentedAt: '2026-09-15T12:26:00-04:00',
				orgTerms: data.memberConsents['demo-haddad-omar']?.orgTerms ?? null,
			},
			'demo-haddad-layla': {
				...data.memberConsents['demo-haddad-layla'],
				consentedAt: '2026-09-15T12:26:00-04:00',
				orgTerms: data.memberConsents['demo-haddad-layla']?.orgTerms ?? null,
			},
		},
	}
}

/** Template data wins for the attached reports. Other variants pass through. */
export function overlayAttachedTemplates(
	report: OverlayReport,
	variant: string,
): OverlayReport {
	// Frozen: Halliday household matches combined-report.html. Align other
	// variants in their own spec — do not retune applyCombined.
	if (variant === 'haddad-household' && report.kind === 'combined') {
		return { kind: 'combined', data: applyCombined(report.data) }
	}
	const detail = DETAIL[variant]
	if (!detail || report.kind !== 'detail') return report
	return { kind: 'detail', data: applyDetail(report.data, detail) }
}
